import {ControlButton, TscButtonType, TscStyles, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import Constants from '../../constants/Constants';
import cssConstants from '../../constants/cssConstants';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {mediaStore} from '../../context/mediaProvider';
import {themeStore} from '../../context/themeProvider';
import Analytics from '../../service/analytics';
import SnagitLogoSvg from '../../static/svg/SnagitLogo.svg';
import PlusGlyph from '../../static/svg/PlusGlyph.svg';
import ScreencastMarketingLogo from '../../static/svg/ScreencastMarketingLogo.svg';
import SnagitScreencastLogo from '../../static/svg/SnagitScreencastLogo.svg';
import CamtasiaLogoSvg from '../../static/svg/CamtasiaLogo.svg';
import {setupCSSHeightObserver} from '../../util/CSSHeightObserver';
import {showWatchPageSnagitMessage, showWatchPageCaptureMessage, showWatchPageCamtasiaMessage} from '../../util/MarketingHelper';

interface IBannerText {
   iconText: string;
   detailText: string;
   buttonText: string;
   smallIconText: string;
   smallDetailText: string;
   smallButtonText: string;
}

const MarketingPane = styled.div<{theme: TscThemeNames; camtasiaBanner: boolean}>`
   box-shadow: ${props => props.camtasiaBanner ? cssConstants.marketingPaneBoxShadowCamtasia : (props.theme === TscThemeName.dusk ? cssConstants.marketingPaneBoxShadowDusk : cssConstants.marketingPaneBoxShadowDawn)};
   font-family: sans-serif;
   display: flex;
   background: ${props => props.theme === TscThemeName.dusk ? (props.camtasiaBanner ? cssConstants.marketingPaneBgCamtasiaDusk : cssConstants.marketingPaneBgDusk) : (props.camtasiaBanner ? cssConstants.marketingPaneBgCamtasiaDawn : cssConstants.marketingPaneBgDawn)};
   background-size: cover;
   background-origin: border-box;
   border-radius: 200px;
   border: ${props => props.theme === TscThemeName.dusk ? (props.camtasiaBanner ? cssConstants.marketingPaneBorderCamtasiaDusk : cssConstants.marketingPaneBorderDusk) : (props.camtasiaBanner ? cssConstants.marketingPaneBorderCamtasiaDawn : cssConstants.marketingPaneBorderDawn)};
   -webkit-box-align: center;
   align-items: center;
   align-content: flex-end;
   height: 1.875rem;
   padding: 0.5rem 0.5rem 0.5rem 1rem;
   margin: 0.8125rem 0.5rem .75rem .5rem;
   color: ${props => props.theme === TscThemeName.dusk || props.camtasiaBanner ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   width: fit-content;

   @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
      min-width: 32rem;
   }

   @media screen and (min-width: ${cssConstants.marketingPaneBreakpoint}px) {
      min-width: 44rem;
      margin-top: 0;
   }

`;

const MarketingContainer = styled.span`
   display: flex;
   -webkit-box-align: center;
   align-items: center;

   .captureLogo {
      display: none;
      @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
         display: flex;
      }
   }

   .smallCaptureLogo {      
      display: flex;
      margin-right: .5rem;
      @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
         display: none;
      }
   }
`;

const TextContainer = styled.span`
   display: flex;
   flex-direction: column;
`;

const ButtonContainer = styled.div<{camtasiaBanner: boolean}>`
   display: flex;
   flex: 0 0 auto;
   align-items: center;
   margin-left: auto;
   -webkit-box-align: center;

   .controlButton {
      border-radius: 27px;
      display: none;
      background-color: ${props => props.camtasiaBanner ? TscStyles.color.brand.camtasia : ''};
      color: ${props => props.camtasiaBanner ? TscStyles.color.text.dark : ''};
      justify-content: center;
      letter-spacing: 0;
      margin: 0 0 0 1rem;
      padding: 0 1rem;

      @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {         
         display: flex;
      }
   }

   .mobileControlButton {
      border-radius: 27px;
      display: flex;
      background-color: ${props => props.camtasiaBanner ? TscStyles.color.brand.camtasia : ''};
      color: ${props => props.camtasiaBanner ? TscStyles.color.text.dark : ''};
      width: auto;
      letter-spacing: 0;
      margin: 0 0 0 .4rem;

      @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {         
         display: none;
      }
   }
`;

const IconSvg = styled.span<{theme: TscThemeNames}>`
   display:flex;

   svg {
      height: 32px;
      width: 32px;
   }
`;

const IconText = styled.span<{captureBanner: boolean}>`
   font-weight: 600;
   font-size: 1rem;
   font-family: "Proxima Nova", proxima-nova, sans-serif;
   line-height: 1rem;
   width: auto;

   @media screen and (min-width: ${cssConstants.marketingPaneBreakpoint}px) {
      margin-right: .5rem;
   }

   .iconText{
      display: none;

      @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
         display: flex;
      }
   }

   .mobileIconText{
      display: flex;
      max-width: 17rem;
      font-size : ${props => props.captureBanner ? '.8125rem' : '1rem'};
      
      @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
         display: none;
      }
   }
`;

const DetailText = styled.span<{captureBanner: boolean}>`
   display: none;
   font-family: "Proxima Nova", proxima-nova, sans-serif;
   margin-top: .25rem;
   font-size: .75rem;
   line-height: .75rem;
   font-weight: 500;

   @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
      display: ${props => props.captureBanner ? 'none' : 'flex'};
   }

   @media screen and (min-width: ${cssConstants.marketingPaneBreakpoint}px) {
      display: flex;
   }
`;

const Logo = styled.span`
   display: flex;
   flex-direction: row;
   margin-right: 0.5rem;
`;

const PlusSignSvg = styled.span`
   display: flex;
   flex-direction: column;
   justify-content: center;
   margin: 0 0.25rem;
   color: red;
`;

const StyledPlusGlyph = styled(PlusGlyph)<{theme: TscThemeNames}>`
   fill: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dawn.lightMedium : TscStyles.color.ui.dusk.medium};
`;

const getMarketingLogo = (theme: TscThemeNames): React.JSX.Element => {
   if (showWatchPageSnagitMessage()) {
      return (
         <Logo className="snagitLogo">
            <IconSvg><SnagitLogoSvg/></IconSvg>
         </Logo>
      );
   }

   if (showWatchPageCaptureMessage()) {
      return (
         <span>
            <Logo className="captureLogo">
               <IconSvg><SnagitLogoSvg/></IconSvg>
               <PlusSignSvg><StyledPlusGlyph theme={theme}/></PlusSignSvg>
               <IconSvg><ScreencastMarketingLogo/></IconSvg>
            </Logo>
            <Logo className="smallCaptureLogo">
               <IconSvg><SnagitScreencastLogo/></IconSvg>
            </Logo>
         </span>
      );
   }

   return (
      <Logo className="camtasiaLogo">
         <IconSvg><CamtasiaLogoSvg/></IconSvg>
      </Logo>
   );
};

export const MarketingPaneBase: React.FC<IStateMappedProps> = ({Hash, theme}) => {
   const marketingPaneRef = React.createRef<HTMLDivElement>();

   const {t} = useTranslation();
   const [randomIndex, setRandomIndex] = useState<number>(0);
   const isSnagitBanner = showWatchPageSnagitMessage();
   const isCaptureBanner = showWatchPageCaptureMessage();
   const isCamtasiaBanner = showWatchPageCamtasiaMessage();

   const bannerTextArray = [];
   const smallBannerTextArray = [];

   // Push all groups of banner text options into bannerTextArray and smallBannerTextArray
   if (isSnagitBanner) {
      bannerTextArray.push([t('marketing.snagitIconText1'), t('marketing.snagitDetailText1'), t('marketing.snagitButtonText1')]);
      smallBannerTextArray.push([t('marketing.snagitIconText1'), t('marketing.snagitDetailText1'), t('marketing.snagitButtonText1')]);
      
      bannerTextArray.push([t('marketing.snagitIconText1'), t('marketing.snagitDetailText1'), t('marketing.snagitButtonText2')]);
      smallBannerTextArray.push([t('marketing.snagitIconText1'), t('marketing.snagitDetailText1'), t('marketing.snagitSmallButtonText2')]);
   } else if (isCaptureBanner) {
      bannerTextArray.push([t('marketing.captureIconText1'), t('marketing.captureDetailText1'), t('marketing.snagitButtonText1')]);
      smallBannerTextArray.push([t('marketing.captureIconText1'), t('marketing.captureDetailText1'), t('marketing.captureSmallButtonText1')]);
      
      bannerTextArray.push([t('marketing.captureIconText1'), t('marketing.captureDetailText1'), t('marketing.captureButtonText2')]);
      smallBannerTextArray.push([t('marketing.captureIconText1'), t('marketing.captureDetailText1'), t('marketing.captureButtonText2')]);
   } else {
      bannerTextArray.push([t('marketing.camtasiaIconText1'), t('marketing.camtasiaDetailText1'), t('marketing.camtasiaButtonText1')]);
      smallBannerTextArray.push([t('marketing.camtasiaSmallIconText1'), t('marketing.camtasiaDetailText1'), t('marketing.camtasiaSmallButtonText1')]);

      bannerTextArray.push([t('marketing.camtasiaIconText1'), t('marketing.camtasiaDetailText2'), t('marketing.camtasiaButtonText1')]);
      smallBannerTextArray.push([t('marketing.camtasiaSmallIconText1'), t('marketing.camtasiaDetailText2'), t('marketing.camtasiaSmallButtonText1')]);
   }

   useEffect(() => {
      // Randomly choose the index for a set of strings that will be displayed on the banner from the bannerTextArray (and smallBannerTextArray)
      const bannerTypeIndex = Math.floor(Math.random() * bannerTextArray.length);
      setRandomIndex(bannerTypeIndex);
      recordGABannerDisplayedEvent(bannerTypeIndex);
   }, []);
   
   const bannerText = {
      iconText: bannerTextArray[randomIndex][0],
      detailText: bannerTextArray[randomIndex][1],
      buttonText: bannerTextArray[randomIndex][2],
      smallIconText: smallBannerTextArray[randomIndex][0],
      smallDetailText: smallBannerTextArray[randomIndex][1],
      smallButtonText: smallBannerTextArray[randomIndex][2]
   } as IBannerText;

   const recordGABannerButtonClickedEvent = () => {
      const action = isSnagitBanner ? Constants.analytics.madeWithSnagitBannerButtonClicked : (isCaptureBanner ? Constants.analytics.madeWithCaptureBannerButtonClicked : Constants.analytics.madeWithCamtasiaBannerButtonClicked);
      const eventAction = `${action}_v${randomIndex + 1}`;
      const gaViewEvent = {
         category: Constants.analytics.promotionsCategory,
         action: eventAction,
         label: Hash
      };
      Analytics.gaEvent(gaViewEvent);
   };

   const recordGABannerDisplayedEvent = (bannerTypeNum: number) => {
      const action = isSnagitBanner ? Constants.analytics.madeWithSnagitBannerDisplayed : (isCaptureBanner ? Constants.analytics.madeWithCaptureBannerDisplayed : Constants.analytics.madeWithCamtasiaBannerDisplayed);
      const eventAction = `${action}_v${bannerTypeNum + 1}`;
      const gaViewEvent = {
         category: Constants.analytics.promotionsCategory,
         action: eventAction,
         label: Hash
      };
      Analytics.gaEvent(gaViewEvent);
   };

   const navigatetoMarketingPage = () => {
      recordGABannerButtonClickedEvent();
      const bannerType = randomIndex + 1;
      const marketingLink = isCamtasiaBanner ? Constants.navigation.screencast.madeWithCamtasiaMarketingLink : Constants.navigation.screencast.madeWithSnagitMarketingLink;
      const viewpage = isCamtasiaBanner ? 'camtasia' : (isSnagitBanner ? 'snagit' : 'snagit-capture');

      window.open(
         marketingLink.replace('{0}', viewpage).replace('{1}', bannerType.toString()),
         '_blank'
      );
   };

   setupCSSHeightObserver(marketingPaneRef, cssConstants.variables.marketingBannerHeight);
   return (
      <MarketingPane theme={theme} ref={marketingPaneRef} camtasiaBanner={isCamtasiaBanner} data-test-id="marketing-pane">
         <MarketingContainer>
            {getMarketingLogo(theme)}
            <TextContainer>
               <IconText captureBanner={isCaptureBanner}>
                  <span className="iconText">{bannerText.iconText}</span>
                  <span className="mobileIconText">{bannerText.smallIconText}</span>
               </IconText>
               <DetailText captureBanner={isCaptureBanner}>
                  <span className="detailText">{bannerText.detailText}</span>
               </DetailText>
            </TextContainer>

         </MarketingContainer>
         <ButtonContainer camtasiaBanner={isCamtasiaBanner}>
            <ControlButton
               className="controlButton"
               themeName={theme}
               buttonType={TscButtonType.primary}
               testId="marketing-call-to-action"
               label={bannerText.buttonText}
               onClick={navigatetoMarketingPage}
            />
            <ControlButton
               className="mobileControlButton"
               themeName={theme}
               buttonType={TscButtonType.primary}
               testId="marketing-call-to-action"
               label={bannerText.smallButtonText}
               onClick={navigatetoMarketingPage}
            />
         </ButtonContainer>
      </MarketingPane>
   );
};

export interface IStateMappedProps {
   theme: TscThemeNames;
   Hash: string;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {theme}: Partial<IStateMappedProps>,
   {Hash}: Partial<IStateMappedProps>
): IStateMappedProps => ({Hash, theme});

export default withMemoizedContexts(mapStatesToProps, themeStore, mediaStore)(MarketingPaneBase);
